import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Label, NativeSelect, Textarea } from "@/components/ui/input";
import { ActionDialog } from "@/components/action-dialog";
import { DataTable } from "@/components/data-table";
import { PageHeader, StatusBadge } from "@/components/common";
import { api } from "@/lib/api";
import { ago } from "@/lib/format";
import { label } from "@/lib/labels";

interface RequestRow {
  id: string;
  origin: string;
  status: string;
  description: string;
  contact: { name: string | null; email: string | null; phone: string | null };
  adminNotes: string | null;
  createdAt: string;
}

const STATUSES = ["new", "contacted", "in_review", "accepted", "rejected"] as const;

function Update({ r }: { r: RequestRow }) {
  const qc = useQueryClient();
  const [status, setStatus] = React.useState(r.status === "new" ? "contacted" : r.status);
  const [notes, setNotes] = React.useState(r.adminNotes ?? "");
  return (
    <ActionDialog
      trigger={
        <Button size="sm" variant="outline">
          Güncelle
        </Button>
      }
      title="Hizmet talebi"
      description={<span className="whitespace-pre-line">{r.description}</span>}
      confirmLabel="Kaydet"
      success="Güncellendi."
      onSubmit={async () => {
        await api(`/v1/admin/service-requests/${r.id}`, {
          method: "PATCH",
          body: { status, adminNotes: notes.trim() || null },
        });
        await qc.invalidateQueries({ queryKey: ["admin-service-requests"] });
      }}
    >
      <div className="grid gap-3">
        <div className="text-sm">
          {r.contact.name ?? "—"} · {r.contact.email ?? "—"}{" "}
          {r.contact.phone && `· ${r.contact.phone}`}
        </div>
        <div className="grid gap-2">
          <Label htmlFor={`s-${r.id}`}>Durum</Label>
          <NativeSelect id={`s-${r.id}`} value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="grid gap-2">
          <Label htmlFor={`n-${r.id}`}>Ekip notu</Label>
          <Textarea
            id={`n-${r.id}`}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={4000}
          />
        </div>
      </div>
    </ActionDialog>
  );
}

export function ServiceRequestsPage() {
  const [status, setStatus] = React.useState("new");
  const q = useQuery({
    queryKey: ["admin-service-requests", status],
    queryFn: () =>
      api<{ requests: RequestRow[] }>(
        `/v1/admin/service-requests?limit=100${status ? `&status=${status}` : ""}`,
      ),
  });
  const columns: ColumnDef<RequestRow, unknown>[] = [
    {
      accessorKey: "description",
      header: "Talep",
      cell: ({ row }) => <span className="line-clamp-2 max-w-lg">{row.original.description}</span>,
    },
    { accessorKey: "origin", header: "Kimden", cell: ({ row }) => label(row.original.origin) },
    { id: "contact", header: "İletişim", cell: ({ row }) => row.original.contact.email ?? "—" },
    {
      accessorKey: "status",
      header: "Durum",
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    { accessorKey: "createdAt", header: "Geldi", cell: ({ row }) => ago(row.original.createdAt) },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Update r={row.original} />
        </div>
      ),
    },
  ];
  return (
    <>
      <PageHeader
        title="Hizmet talepleri"
        description="“Aradığın hizmet yok mu?” formundan gelenler (K-011, K-035)."
        actions={
          <NativeSelect
            aria-label="Durum"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">Tümü</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </NativeSelect>
        }
      />
      <DataTable
        columns={columns}
        data={q.data?.requests}
        isLoading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty="Talep yok."
      />
    </>
  );
}
